import { PillButton } from '@rocco/components/pill-button';
import { useFiltersContext } from '@rocco/ui/filter-sort/components/Filters';
import type { FilterConfigModalItem } from '@rocco/ui/filter-sort/types/filter-config-modal';
import { resolveSectionFilterState } from '@rocco/ui/filter-sort/components/filters-modal/refine/get-refine-presentation';
import type { FilterState } from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import {
    isMultipleValuesFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type {
    FilterStates,
    FilterStatesKey,
} from '@rocco/ui/filter-sort/view-models/filter-states';

interface RefinePillGroupProps {
    filters: FilterConfigModalItem[];
    filterStates: FilterStates;
    onUpdateFilterState: (
        filterKey: FilterStatesKey,
        newState: FilterState,
        shouldAggregate?: boolean,
    ) => void;
}

/**
 * An always-visible pill group for small enums: boolean toggles render as
 * one pill each, multi-value enums as one pill per option. No accordion
 * door — the options themselves carry the information scent.
 */
export const RefinePillGroup = (props: RefinePillGroupProps) => {
    const { filters, filterStates, onUpdateFilterState } = props;
    const {
        searchFilters: { handleToggleMultipleValues },
    } = useFiltersContext();

    return (
        <div className="flex flex-wrap gap-2 pb-3">
            {filters.map((filter) => {
                const state = resolveSectionFilterState(filter, filterStates);
                if (!state) return null;

                if (isSingleValueFilterState(state)) {
                    return (
                        <PillButton
                            key={filter.filterKey}
                            size="sm"
                            selected={state.checked}
                            disabled={state.disabled}
                            aria-pressed={state.checked}
                            onClick={() =>
                                onUpdateFilterState(filter.filterKey, {
                                    ...state,
                                    checked: !state.checked,
                                })
                            }
                        >
                            {filter.label ?? state.label}
                        </PillButton>
                    );
                }

                if (isMultipleValuesFilterState(state)) {
                    return state.items.map((item) => (
                        <PillButton
                            key={`${filter.filterKey}:${String(item.value)}`}
                            size="sm"
                            selected={item.checked}
                            disabled={item.disabled}
                            aria-pressed={item.checked}
                            onClick={() => {
                                if (!handleToggleMultipleValues) return;
                                onUpdateFilterState(
                                    filter.filterKey,
                                    handleToggleMultipleValues(
                                        state,
                                        item,
                                        !item.checked,
                                    ),
                                );
                            }}
                        >
                            {item.label}
                        </PillButton>
                    ));
                }

                return null;
            })}
        </div>
    );
};
